import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  FolderOpen, FolderPlus, Trash2, Printer, Mail, Phone, Globe, MapPin,
  ArrowLeft, X, Pencil,
} from 'lucide-react';

const MyReferralFolders = () => {
  const [folders, setFolders] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ client_email: '', client_name: '', message: '' });
  const [sending, setSending] = useState(false);

  const loadFolders = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API_URL}/resource-folders`); setFolders(res.data.folders || []); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  const openFolder = async (id) => {
    try { const res = await axios.get(`${API_URL}/resource-folders/${id}`); setActive(res.data.folder); }
    catch (e) { toast.error('Could not open folder.'); }
  };

  useEffect(() => { document.title = 'My Referral Folders | I Need A Social Worker'; loadFolders(); }, [loadFolders]);

  const createFolder = async () => {
    if (!newName.trim()) return;
    try { await axios.post(`${API_URL}/resource-folders`, { name: newName.trim() }); setNewName(''); toast.success('Folder created.'); await loadFolders(); }
    catch (e) { toast.error('Could not create folder.'); }
  };

  const deleteFolder = async (id) => {
    if (!window.confirm('Delete this folder?')) return;
    try { await axios.delete(`${API_URL}/resource-folders/${id}`); if (active?.folder_id === id) setActive(null); await loadFolders(); toast.success('Deleted.'); }
    catch (e) { toast.error('Could not delete.'); }
  };

  const rename = async (folder) => {
    const name = window.prompt('Rename folder:', folder.name);
    if (!name || !name.trim() || name.trim() === folder.name) return;
    try { await axios.put(`${API_URL}/resource-folders/${folder.folder_id}`, { name: name.trim() }); await loadFolders(); if (active?.folder_id === folder.folder_id) openFolder(folder.folder_id); toast.success('Renamed.'); }
    catch (e) { toast.error('Could not rename.'); }
  };

  const removeResource = async (rid) => {
    try { await axios.post(`${API_URL}/resource-folders/${active.folder_id}/remove`, { resource_id: rid }); await openFolder(active.folder_id); await loadFolders(); }
    catch (e) { toast.error('Could not remove.'); }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/resource-folders/${active.folder_id}/email`, emailForm);
      toast.success(res.data.message);
      setEmailOpen(false); setEmailForm({ client_email: '', client_name: '', message: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send.'); } finally { setSending(false); }
  };

  const printList = () => window.print();

  if (loading) return <div className="min-h-screen bg-gray-50 pt-24 text-center text-gray-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 no-print">
          <div>
            <p className="text-secondary font-semibold uppercase tracking-widest text-xs flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Community Resource Directory</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-primary mt-1" data-testid="folders-title">My Referral Folders</h1>
          </div>
          <Link to="/community/resources" className="text-primary hover:text-secondary text-sm font-medium">Browse directory →</Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Folder list */}
          <div className="lg:col-span-1 no-print">
            <div className="bg-white rounded-2xl border p-5">
              <div className="flex gap-2 mb-4">
                <Input placeholder="New folder (e.g. client name)" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-folder-input" />
                <Button className="bg-primary text-white" onClick={createFolder} data-testid="create-folder-btn"><FolderPlus className="w-4 h-4" /></Button>
              </div>
              {folders.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No folders yet. Create one, then save resources from the directory.</p>
              ) : (
                <ul className="space-y-2">
                  {folders.map((f) => (
                    <li key={f.folder_id}>
                      <div className={`flex items-center justify-between px-3 py-2 rounded-lg border cursor-pointer ${active?.folder_id === f.folder_id ? 'border-primary bg-primary/5' : 'hover:border-gray-300'}`} onClick={() => openFolder(f.folder_id)} data-testid={`folder-${f.folder_id}`}>
                        <span className="text-gray-800 text-sm">{f.name} <span className="text-xs text-gray-400">({f.item_count})</span></span>
                        <span className="flex gap-1">
                          <button onClick={(e) => { e.stopPropagation(); rename(f); }} className="text-gray-400 hover:text-primary p-1"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={(e) => { e.stopPropagation(); deleteFolder(f.folder_id); }} className="text-gray-400 hover:text-red-600 p-1" data-testid={`delete-folder-${f.folder_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Folder detail */}
          <div className="lg:col-span-2">
            {!active ? (
              <div className="bg-white rounded-2xl border p-10 text-center text-gray-500 no-print">Select a folder to view, print, or email its referral list.</div>
            ) : (
              <div className="bg-white rounded-2xl border p-6" id="printable-referral">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                  <h2 className="font-serif text-2xl font-bold text-primary">{active.name}</h2>
                  <div className="flex gap-2 no-print">
                    <Button size="sm" variant="outline" onClick={printList} data-testid="print-folder-btn"><Printer className="w-4 h-4 mr-1" /> Print</Button>
                    <Button size="sm" className="bg-secondary text-white" onClick={() => setEmailOpen(true)} data-testid="email-folder-btn"><Mail className="w-4 h-4 mr-1" /> Email to Client</Button>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-4 hidden print:block">Community Resource Referrals — INeedASocialWorker.com</p>
                {(active.resources || []).length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">This folder is empty. Save resources from the <Link to="/community/resources" className="text-secondary underline">directory</Link>.</p>
                ) : (
                  <div className="space-y-3">
                    {active.resources.map((r) => (
                      <div key={r.resource_id} className="border rounded-xl p-4" data-testid={`folder-resource-${r.resource_id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className="text-xs font-semibold text-secondary uppercase">{r.category}</span>
                            <h4 className="font-serif text-lg font-bold text-primary">{r.name}</h4>
                          </div>
                          <button onClick={() => removeResource(r.resource_id)} className="text-gray-300 hover:text-red-600 no-print" data-testid={`remove-resource-${r.resource_id}`}><X className="w-4 h-4" /></button>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{r.description}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                          {r.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {r.phone}</span>}
                          {r.website && <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> {r.website}</span>}
                          {(r.city || r.state) && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {[r.city, r.state].filter(Boolean).join(', ')}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-4">Information is subject to change without notice.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Email Referral List</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-gray-500 mb-1 block">Client email *</Label><Input type="email" value={emailForm.client_email} onChange={(e) => setEmailForm((f) => ({ ...f, client_email: e.target.value }))} data-testid="email-client-address" placeholder="client@example.com" /></div>
            <div><Label className="text-xs text-gray-500 mb-1 block">Client name</Label><Input value={emailForm.client_name} onChange={(e) => setEmailForm((f) => ({ ...f, client_name: e.target.value }))} /></div>
            <div><Label className="text-xs text-gray-500 mb-1 block">Personal message (optional)</Label><Textarea rows={3} value={emailForm.message} onChange={(e) => setEmailForm((f) => ({ ...f, message: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button className="bg-primary text-white" onClick={sendEmail} disabled={sending} data-testid="send-referral-email"><Mail className="w-4 h-4 mr-1" /> {sending ? 'Sending…' : 'Send'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyReferralFolders;
